import { z } from "zod";

/**
 * Окно переписки: те же объявления и ответы, собранные не лентой карточек, а
 * разговорами (решение владельца 29.08.2026, «как в Telegram и WhatsApp»).
 *
 * ⚠️ **Разговор это АДРЕСАТ, а не сущность.** Ни одной таблицы под него нет и
 * заводить её не надо: разговор это ВЫБОРКА из `announcement`,
 * `announcement_recipient` и `announcement_reply`. Ключ разговора собирается из
 * того, что и так лежит в этих таблицах, и живёт он в двух функциях ниже
 * (`conversationId` и `parseConversationId`), а не в разборе строки на каждом
 * читателе.
 *
 * Правило раскладки, из которого следует всё остальное:
 *
 * | что отправили | окно у преподавателя | окно у ученика |
 * |---|---|---|
 * | группе с ВИДИМЫМ составом | разговор группы | разговор группы |
 * | группе со скрытым составом | разговор группы | ЛИЧНОЕ окно с преподавателем |
 * | ученикам поимённо | по личному окну на каждого | личное окно с преподавателем |
 *
 * ⚠️ **Вторая строка это не оплошность, а граница приватности.** В скрытой
 * группе ученик не знает ни состава, ни численности, ни факта своего членства
 * (раздел 8 `docs/group-lessons-design.md`), значит разговора группы у него быть
 * не может вовсе: само наличие такого окна и было бы раскрытием. Третья строка
 * это поведение рассылки в WhatsApp: отправитель видит список, каждый получатель
 * видит обычное личное сообщение.
 *
 * ⚠️ **Название ВИДИМОЙ группы едет её участникам** (решение владельца
 * 30.08.2026), поэтому групповое окно подписано у обеих сторон одинаково.
 * Прежнее правило «название только преподавателю» писалось тогда, когда ученик
 * о группе не знал вовсе; у видимой группы эта посылка мертва, потому что
 * поимённый состав он уже получает, а «Девятый класс» раскрывает меньше, чем
 * имена. У скрытой группы окна группы у ученика нет вовсе (вторая строка
 * таблицы), значит и вопроса о подписи там не возникает.
 *
 * ⚠️ **Опрос это свежесть в открытой вкладке, а не уведомления.** Транспорта
 * уведомлений у продукта нет (раздел 4 `CLAUDE.md` запрещает Telegram для
 * критичного, почты нет), и ни одно поле здесь не обещает доставку:
 * `ConversationUnreadResponse` отвечает на вопрос «что изменилось с моего
 * прошлого запроса», а не «человеку сообщили».
 */

/* ──────────────────────────── потолки ──────────────────────────── */

/**
 * Сколько разговоров едет в списке за раз.
 *
 * ⚠️ **Число посчитано в байтах на проводе, настоящим JSON после gzip**, тем же
 * способом и на той же русской прозе, что считался `FEED_LIMIT` у снятой ленты
 * объявлений. Метод сверен: воспроизведённая лента дала 105,6 / 33,9 КБ там, где
 * у `FEED_LIMIT` было записано 106,5 / 33,5 КБ.
 *
 * Худшая строка это заголовок в `GROUP_TITLE_MAX` знаков, имя автора в 80
 * (потолок `DisplayName`) и превью во весь `CONVERSATION_PREVIEW_MAX`:
 *
 * | что | сырых | сжатых |
 * |---|---|---|
 * | 20 худших строк | 17,1 КБ | 6,4 КБ |
 * | 30 худших строк | 25,9 КБ | 9,2 КБ |
 * | 40 худших строк | 34,5 КБ | 11,8 КБ |
 * | **50 худших строк** | **42,7 КБ** | **14,2 КБ** |
 * | 60 худших строк (интерполяция) | ~51 КБ | ~17 КБ |
 * | 50 обычных строк (имена, короткие названия) | 22,2 КБ | 7,0 КБ |
 *
 * Отсюда пятьдесят: это наибольшее число, при котором в отсечку около 16 КБ
 * (раздел 3 `CLAUDE.md`) влезает ХУДШИЙ список, а не типичный. Запас те же
 * два килобайта, что считал достаточными `FEED_LIMIT` (14,3 КБ).
 *
 * ⚠️ Курсора у списка разговоров нет, и это осознанный долг: разговоры
 * сортируются по ВЫЧИСЛЯЕМОЙ свежести, то есть keyset-курсор пришлось бы
 * снимать с агрегата. Пятьдесят это заведомо больше, чем групп и учеников у
 * одного преподавателя в первый год; что список обрезан, говорит `truncated`.
 */
export const CONVERSATION_LIST_LIMIT = 50;

/**
 * Сколько знаков тела едет в превью строки списка.
 *
 * Строка списка это одна строчка на экране, и возить туда объявление на 2000
 * знаков незачем: полсотни таких строк это 200 КБ сырых. Обрезает СЕРВЕР, а не
 * клиент, по той же причине, по которой признак усечения ленты считает сервер:
 * иначе в бандл уедет и правило обрезки, и целое тело, которое оно обрезает.
 */
export const CONVERSATION_PREVIEW_MAX = 120;

/* ─────────────────────── ключ разговора ────────────────────────── */

export const ConversationKind = z.enum(["group", "person"]);
export type ConversationKind = z.infer<typeof ConversationKind>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ключ разговора одной строкой: вид и тот, с кем разговор.
 *
 * `group:<id группы>` либо `person:<id второй стороны>`. Вторая сторона это
 * ученик, если читатель преподаватель, и преподаватель, если читатель ученик:
 * роль вычисляется позиционно и в ключ не входит.
 *
 * ⚠️ **Сборка и разбор живут ЗДЕСЬ, а не в маршруте и не на экране.** Ключ
 * ездит в адресе трёх ручек и в двух списках, а строка, которую четыре места
 * собирают конкатенацией, разъедется на первой же правке разделителя.
 */
export const ConversationId = z.string().regex(/^(group|person):[0-9a-f-]{36}$/i);
export type ConversationId = z.infer<typeof ConversationId>;

export interface ConversationKey {
  kind: ConversationKind;
  /** Группа у `group`, вторая сторона разговора у `person`. */
  otherId: string;
}

export function conversationId(key: ConversationKey): string {
  return `${key.kind}:${key.otherId}`;
}

/**
 * Разбор ключа. `null` означает «мусор», и вызывающий обязан ответить 404, а не
 * молча показать что-нибудь: кривой ключ снаружи неотличим от несуществующего
 * разговора, и различать их значило бы подсказывать перебирающему (раздел 9
 * `CLAUDE.md`).
 */
export function parseConversationId(raw: string): ConversationKey | null {
  const colon = raw.indexOf(":");
  if (colon <= 0) return null;
  const kind = ConversationKind.safeParse(raw.slice(0, colon));
  if (!kind.success) return null;
  const otherId = raw.slice(colon + 1);
  if (!UUID.test(otherId)) return null;
  return { kind: kind.data, otherId };
}

/* ──────────────────────── список разговоров ────────────────────── */

/**
 * Последнее сообщение разговора, как его показывают в списке.
 *
 * `body` уже обрезано сервером до `CONVERSATION_PREVIEW_MAX`. `null` означает
 * «стёрто по сроку хранения» либо «удалено», и разбирает эту пару та же
 * `announcementReplyState`, что и в нитке: полей у неё те же два, `body` и
 * `deletedAt`, поэтому второго разбора заводить не надо.
 */
export const ConversationPreview = z.object({
  /** Как автора зовут ДЛЯ ЭТОГО ЧИТАТЕЛЯ. Собирается там же, где в нитке. */
  authorName: z.string(),
  /** Моё ли это сообщение. Своего идентификатора клиенту для этого не нужно. */
  mine: z.boolean(),
  body: z.string().nullable(),
  createdAt: z.number().int(),
  deletedAt: z.number().int().nullable(),
});
export type ConversationPreview = z.infer<typeof ConversationPreview>;

/**
 * Строка списка окон.
 *
 * ⚠️ **Роли читателя здесь НЕТ, и это снятие 30.08.2026.** Поле `myRole` тут
 * стояло с первого дня фичи и не пригодилось ни разу: строка списка спрашивает
 * «что это за окно и что в нём написали последним», и отвечают на это `kind` и
 * `last.mine`. Роль нужна там, где от неё зависит вид экрана, то есть в шапке
 * открытого окна (`ConversationPageResponse.myRole`), и там она осталась. Вес
 * поля, которое никто не читает, умножается на полсотни строк и на каждый заход
 * в раздел.
 */
export const ConversationSummary = z.object({
  id: ConversationId,
  kind: ConversationKind,
  /**
   * Как назвать окно ЭТОМУ читателю: название группы у группового разговора,
   * имя второй стороны у личного.
   *
   * ⚠️ **Название группы едет обеим сторонам, и это правка 30.08.2026.** До неё
   * оно уезжало только преподавателю, и групповое окно у ученика было подписано
   * именем преподавателя, то есть два его окна с одним человеком выглядели
   * одинаково. Раскрытием название не становится: групповой разговор существует
   * у ученика только при видимом составе, а поимённый состав приезжает ему в
   * шапке окна (`ConversationPageResponse.members`).
   */
  title: z.string(),
  /**
   * Сколько сообщений собеседника я ещё не видел. Правило одно на обе роли, и
   * разбор стоит у `ConversationUnreadResponse`.
   */
  unread: z.number().int().nonnegative(),
  /** Разговора без единого сообщения не бывает: он ими и определяется. */
  last: ConversationPreview,
});
export type ConversationSummary = z.infer<typeof ConversationSummary>;

export const ConversationListResponse = z.object({
  items: z.array(ConversationSummary),
  /**
   * Осталось ли за списком хоть один разговор. Признак с сервера, а не потолок
   * в контрактах: потолок это внутренняя арифметика запроса, и клиент,
   * сравнивающий длину списка с числом, повторял бы чужой расчёт.
   */
  truncated: z.boolean(),
});
export type ConversationListResponse = z.infer<typeof ConversationListResponse>;

/* ─────────────────── счётчик непрочитанного ────────────────────── */

export const ConversationUnread = z.object({
  id: ConversationId,
  unread: z.number().int().nonnegative(),
  /** Время последнего сообщения. По нему открытое окно узнаёт, что пора перечитать. */
  at: z.number().int(),
});
export type ConversationUnread = z.infer<typeof ConversationUnread>;

/**
 * Ответ дешёвой ручки, которую опрашивают раз в пять секунд: ни одного тела
 * сообщения, ни одного имени, три поля на разговор.
 *
 * | строк | сырых | сжатых |
 * |---|---|---|
 * | 30 | 2,4 КБ | 0,8 КБ |
 * | 50 | 4,1 КБ | 1,3 КБ |
 *
 * ⚠️ **Правило ОДНО на обе роли: непрочитано то, что написал собеседник после
 * моего указателя** (решение владельца 30.08.2026). Указатель это своя таблица
 * `conversation_read`, и двигает его открытие разговора у обеих сторон.
 *
 * До неё счётчики считались РАЗНЫМ, потому что единственным состоянием чтения
 * была `announcement_recipient.seen_at`, а строки получателя у преподавателя нет
 * вовсе: у ученика считались неоткрытые объявления, у преподавателя ответы после
 * его последнего слова. Обе приблизительности были видны пользователю. Счётчик
 * преподавателя не гасился открытием окна, только ответом, то есть висел
 * постоянной меткой; а ответ преподавателя не двигал счётчик ученика вовсе.
 * Просили подсказку «сколько новых сообщений», а не «сколько ждут ответа»:
 * число, которое висит, пока не ответишь, перестают читать через неделю, и тогда
 * подсказка хуже её отсутствия.
 *
 * ⚠️ **Квитанция при этом жива и своей работы не потеряла.** Она стоит на
 * ОБЪЯВЛЕНИИ, держит время первого открытия и отвечает преподавателю на вопрос
 * «кто прочитал»; указатель стоит на РАЗГОВОРЕ и отвечает читателю на вопрос
 * «что тут нового». Ровно это различение уже проведено между
 * `app_user.changelog_seen_at` и `identity.last_login_at`
 * (`docs/whats-new-design.md`).
 *
 * Как читается отсутствие указателя (тем же приёмом, что у `changelog_seen_at`:
 * одним выражением при чтении, а не засыпкой), разобрано в
 * `apps/api/src/repo/conversation-scope.ts` у `read_mark`.
 */
export const ConversationUnreadResponse = z.object({
  /** Сумма по ВСЕМ разговорам, а не по приехавшим строкам. */
  total: z.number().int().nonnegative(),
  /**
   * Строки самых свежих разговоров, не больше `CONVERSATION_LIST_LIMIT`.
   *
   * ⚠️ **Открытое окно приезжает сюда ВСЕГДА, где бы оно ни стояло по свежести**,
   * и просит об этом клиент: `?id=<ключ разговора>` поднимает свой разговор в
   * начало сортировки. Без этого счётчик слепнул на пятьдесят первом разговоре
   * молча: строки за потолком не приезжало, окно не узнавало о своей почте
   * НИКОГДА, а `total` при этом её считал, то есть число у раздела росло, и
   * открытое окно не перечитывалось (правка 30.08.2026, молчаливая деградация
   * запрещена разделом 5.5 CLAUDE.md прямо).
   *
   * ⚠️ `id` это ПОДСКАЗКА, а не адрес: незнакомый или мусорный ключ ничего не
   * поднимает и ничем не отказывает. Отказ был бы хуже поломки, которую он
   * ловит: разговор можно удалить из соседней вкладки, и 404 на счётчик увёл бы
   * весь опрос в отступ из-за одного исчезнувшего окна.
   */
  items: z.array(ConversationUnread),
});
export type ConversationUnreadResponse = z.infer<typeof ConversationUnreadResponse>;
